import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  MapBuiltInToolId,
  MapDrawnGeometry,
  MapViewerControlItem,
  MapWorkbenchToolRuntimeEvent,
  MapWorkbenchViewState,
} from '@workspace/ui';
import { computeMapGeometryBounds, MapAuthoringView } from '@workspace/ui';
import { MousePointer, Pentagon, Trash2 } from 'lucide-react';

import { DescriptionText } from '@workspace/silk-ui';

import { usePreferences } from '@/context/PreferencesContext';
import {
  buildMapSourceArchiveUrl,
  buildMapSourceGeoJSONDataUrl,
} from '@/lib/mapping-assets';
import type { MapSource } from '@/lib/mapping-api';
import { findSalesMapVectorLayer } from '@/lib/salesMapSourcePackage';
import { composeStyleWithSystemBasemap } from '@/lib/systemBasemap';
import type { ZoneInfrastructureBinding } from './zoneInfrastructure';

type ParsedGeometry =
  | { kind: 'polygon'; ring: [number, number][] }
  | null;

function parseGeometry(metadata: Record<string, unknown>): ParsedGeometry {
  if (metadata.geometry && typeof metadata.geometry === 'object' && !Array.isArray(metadata.geometry)) {
    const geometry = metadata.geometry as Record<string, unknown>;
    if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
      const firstRing = (geometry.coordinates as unknown[])[0];
      if (Array.isArray(firstRing) && (firstRing as unknown[]).length >= 4) {
        return { kind: 'polygon', ring: firstRing as [number, number][] };
      }
    }
  }
  if (Array.isArray(metadata.polygon) && (metadata.polygon as unknown[]).length >= 3) {
    return { kind: 'polygon', ring: metadata.polygon as [number, number][] };
  }
  if (Array.isArray(metadata.polygons)) {
    const first = (metadata.polygons as unknown[])[0];
    if (Array.isArray(first) && (first as unknown[]).length >= 3) {
      return { kind: 'polygon', ring: first as [number, number][] };
    }
  }
  return null;
}

function closedRing(points: [number, number][]): [number, number][] {
  if (points.length === 0) {
    return points;
  }
  const [firstLng, firstLat] = points[0];
  const [lastLng, lastLat] = points[points.length - 1];
  if (firstLng === lastLng && firstLat === lastLat) {
    return points;
  }
  return [...points, points[0]];
}

function geometryToDrawnGeometry(geometry: ParsedGeometry): MapDrawnGeometry | null {
  if (!geometry || geometry.kind !== 'polygon') {
    return null;
  }
  const ring = closedRing(geometry.ring);
  if (ring.length < 4) {
    return null;
  }
  return {
    geometry_kind: 'polygon',
    coordinates: ring.slice(0, -1),
  };
}

function buildZoneGeometryViewState(geometry: ParsedGeometry): MapWorkbenchViewState | null {
  if (!geometry || geometry.kind !== 'polygon') {
    return null;
  }
  const bounds = computeMapGeometryBounds({
    type: 'Polygon',
    coordinates: [closedRing(geometry.ring)],
  });
  if (!bounds) {
    return null;
  }
  const [west, south, east, north] = bounds;
  const lngSpan = Math.abs(east - west);
  const latSpan = Math.abs(north - south);
  const maxSpan = Math.max(lngSpan, latSpan);
  let zoom = 11;
  if (maxSpan > 40) {
    zoom = 3;
  } else if (maxSpan > 20) {
    zoom = 4;
  } else if (maxSpan > 10) {
    zoom = 5;
  } else if (maxSpan > 5) {
    zoom = 6;
  } else if (maxSpan > 2) {
    zoom = 8;
  } else if (maxSpan > 1) {
    zoom = 9;
  }
  return {
    center: [(west + east) / 2, (south + north) / 2],
    zoom,
    bearing: 0,
    pitch: 0,
  };
}

const DEFAULT_ZONE_AUTHORING_VIEW_STATE = {
  center: [-98.58, 39.83] as [number, number],
  zoom: 3,
  bearing: 0,
  pitch: 0,
};

function clearZoneGeometryMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
  const updated = { ...metadata };
  for (const key of ['polygon', 'polygons', 'bounds', 'circle', 'geometry']) {
    delete updated[key];
  }
  return updated;
}

export interface ZoneMapEditorProps {
  metadata: Record<string, unknown>;
  onMetadataChange: (meta: Record<string, unknown>) => void;
  infrastructureBindings?: ZoneInfrastructureBinding[];
}

type ZoneInfrastructureOverlay = {
  sources: Record<string, Record<string, unknown>>;
  layers: Array<Record<string, unknown>>;
  hasAccessPointContext: boolean;
  hasFiberContext: boolean;
};

function sanitizeOverlayId(value: string, fallback: string) {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
}

function isGeoJSONRuntimeSource(source?: Pick<MapSource, 'source_kind'> | null) {
  return source?.source_kind === 'geojson_http' || source?.source_kind === 'geojson_managed';
}

function buildZoneInfrastructureSource(source: Pick<MapSource, 'id' | 'source_kind'>): Record<string, unknown> | null {
  if (!source.id) {
    return null;
  }
  if (isGeoJSONRuntimeSource(source)) {
    return {
      type: 'geojson',
      data: buildMapSourceGeoJSONDataUrl(source.id),
    };
  }
  return {
    type: 'vector',
    url: `pmtiles://${buildMapSourceArchiveUrl(source.id)}`,
  };
}

function buildZoneInfrastructureOverlay(bindings: ZoneInfrastructureBinding[] = []): ZoneInfrastructureOverlay {
  const overlay: ZoneInfrastructureOverlay = {
    sources: {},
    layers: [],
    hasAccessPointContext: false,
    hasFiberContext: false,
  };
  const seenFiberLayers = new Set<string>();

  bindings.forEach((binding, index) => {
    const sourceId = binding.mapSource.id?.trim();
    if (!sourceId) {
      return;
    }
    const styleSource = buildZoneInfrastructureSource(binding.mapSource);
    if (!styleSource) {
      return;
    }
    const baseId = sanitizeOverlayId(
      `${binding.accessPointLayer.purpose}-${binding.accessPointLayer.layer_id}-${sourceId}`,
      `infrastructure-${index + 1}`,
    );
    const styleSourceId = `zone-infrastructure-${baseId}`;
    overlay.sources[styleSourceId] = styleSource;

    const accessPointVectorLayer = findSalesMapVectorLayer(binding.mapSource, 'fiber_access_points');
    const sourceLayerFor = (layerId: string | null) =>
      styleSource.type === 'vector' && layerId ? { 'source-layer': layerId } : {};

    if (accessPointVectorLayer?.effective_geometry === 'point') {
      overlay.hasAccessPointContext = true;
      overlay.layers.push({
        id: `${baseId}-access-points`,
        type: 'circle',
        source: styleSourceId,
        ...sourceLayerFor(accessPointVectorLayer.id),
        paint: {
          'circle-radius': 4,
          'circle-color': 'hsl(222 47% 11%)',
          'circle-stroke-color': 'hsl(210 40% 98%)',
          'circle-stroke-width': 1.5,
        },
      });
    }

    const fiberMapSource = binding.fiberMapSource ?? binding.mapSource;
    const fiberSourceId = fiberMapSource.id?.trim();
    const fiberVectorLayer = findSalesMapVectorLayer(fiberMapSource, 'network_fiber');
    if (!fiberSourceId || !fiberVectorLayer || fiberVectorLayer.effective_geometry !== 'line') {
      return;
    }
    const fiberLayerKey = `${fiberSourceId}:${fiberVectorLayer.id}`;
    if (seenFiberLayers.has(fiberLayerKey)) {
      overlay.hasFiberContext = true;
      return;
    }
    seenFiberLayers.add(fiberLayerKey);
    const fiberStyleSourceId =
      fiberSourceId === sourceId
        ? styleSourceId
        : `zone-infrastructure-${sanitizeOverlayId(`network-fiber-${fiberSourceId}`, `fiber-${index + 1}`)}`;
    if (fiberStyleSourceId !== styleSourceId) {
      const fiberStyleSource = buildZoneInfrastructureSource(fiberMapSource);
      if (!fiberStyleSource) {
        return;
      }
      overlay.sources[fiberStyleSourceId] = fiberStyleSource;
    }
    const fiberSourceLayerFor = (layerId: string | null) =>
      overlay.sources[fiberStyleSourceId]?.type === 'vector' && layerId ? { 'source-layer': layerId } : {};
    overlay.hasFiberContext = true;
    overlay.layers.push(
      {
        id: `${baseId}-fiber-casing`,
        type: 'line',
        source: fiberStyleSourceId,
        ...fiberSourceLayerFor(fiberVectorLayer.id),
        paint: {
          'line-color': 'hsl(0 0% 100%)',
          'line-width': 4,
          'line-opacity': 0.95,
        },
      },
      {
        id: `${baseId}-fiber-line`,
        type: 'line',
        source: fiberStyleSourceId,
        ...fiberSourceLayerFor(fiberVectorLayer.id),
        paint: {
          'line-color': 'hsl(190 90% 38%)',
          'line-width': 2,
          'line-opacity': 0.9,
        },
      },
    );
  });

  return overlay;
}

export async function buildZoneEditorMapStyle(
  basemapTheme: import('@/context/PreferencesContext').MapBasemapTheme,
  infrastructureOverlay: ZoneInfrastructureOverlay = buildZoneInfrastructureOverlay(),
) {
  return composeStyleWithSystemBasemap(
    {
      version: 8,
      sources: infrastructureOverlay.sources,
      layers: infrastructureOverlay.layers,
    },
    basemapTheme,
  );
}

export function buildZonePolygonMetadata(points: [number, number][]): Record<string, unknown> {
  const ring = closedRing(points);
  return {
    polygon: points,
    geometry: {
      type: 'Polygon',
      coordinates: [ring],
    },
  };
}

export function ZoneMapEditor({
  metadata,
  onMetadataChange,
  infrastructureBindings = [],
}: ZoneMapEditorProps) {
  const { basemapTheme } = usePreferences();
  const [styleJson, setStyleJson] = useState<Record<string, unknown> | null>(null);
  const [activeToolId, setActiveToolId] = useState<MapBuiltInToolId>('select');
  const parsedGeometry = useMemo(() => parseGeometry(metadata), [metadata]);
  const resolvedGeometry = useMemo(() => geometryToDrawnGeometry(parsedGeometry), [parsedGeometry]);
  const resolvedViewState = useMemo(() => buildZoneGeometryViewState(parsedGeometry), [parsedGeometry]);
  const initialViewState = useMemo(
    () => resolvedViewState ?? DEFAULT_ZONE_AUTHORING_VIEW_STATE,
    [resolvedViewState],
  );
  const infrastructureOverlay = useMemo(
    () => buildZoneInfrastructureOverlay(infrastructureBindings),
    [infrastructureBindings],
  );
  const [drawnGeometry, setDrawnGeometry] = useState<MapDrawnGeometry | null>(resolvedGeometry);
  const inspectableLayerIds = useMemo<string[]>(() => [], []);

  useEffect(() => {
    setDrawnGeometry(resolvedGeometry);
  }, [resolvedGeometry]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const nextStyle = await buildZoneEditorMapStyle(basemapTheme, infrastructureOverlay);
      if (!cancelled) {
        setStyleJson(nextStyle);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [basemapTheme, infrastructureOverlay]);

  const commitPolygon = useCallback(
    (geometry: MapDrawnGeometry | null) => {
      if (!geometry || geometry.geometry_kind !== 'polygon' || geometry.coordinates.length < 3) {
        return;
      }
      onMetadataChange({
        ...clearZoneGeometryMetadata(metadata),
        ...buildZonePolygonMetadata(geometry.coordinates),
      });
    },
    [metadata, onMetadataChange],
  );

  const handleToolRuntimeEvent = useCallback(
    (event: MapWorkbenchToolRuntimeEvent) => {
      if (event.kind === 'drawing_cleared') {
        setDrawnGeometry(null);
        setActiveToolId('select');
        onMetadataChange(clearZoneGeometryMetadata(metadata));
        return;
      }
      if (
        (event.kind === 'drawing_completed' || (event.kind === 'drawing_updated' && event.draft === false)) &&
        event.geometry.geometry_kind === 'polygon'
      ) {
        setDrawnGeometry(event.geometry);
        commitPolygon(event.geometry);
        setActiveToolId('select');
      }
    },
    [commitPolygon, metadata, onMetadataChange],
  );

  const clearGeometry = useCallback(() => {
    setDrawnGeometry(null);
    setActiveToolId('select');
    onMetadataChange(clearZoneGeometryMetadata(metadata));
  }, [metadata, onMetadataChange]);

  const hintText =
    activeToolId === 'draw_polygon'
      ? drawnGeometry
        ? 'Click the map to replace the polygon, or drag vertices to adjust it. Double-click to finish the updated polygon.'
        : 'Click to place polygon vertices. Double-click to finish the zone boundary.'
      : 'Use the shared runtime polygon tool to author the zone boundary.';
  const infrastructureHintText = infrastructureOverlay.hasAccessPointContext
    ? infrastructureOverlay.hasFiberContext
      ? 'Active access points and fiber linework are shown as read-only infrastructure context while you draw the zone.'
      : 'Active access points are shown as read-only infrastructure context while you draw the zone.'
    : 'Infrastructure context unavailable. Activate a quote-estimate access-point layer with a bound map source to show access points and fiber while drawing zones.';

  const toolItems = useMemo<MapViewerControlItem[]>(
    () => [
      {
        id: 'select',
        label: 'Select',
        icon: <MousePointer className="h-4 w-4" />,
        active: activeToolId === 'select',
      },
      {
        id: 'draw_polygon',
        label: 'Draw polygon',
        icon: <Pentagon className="h-4 w-4" />,
        active: activeToolId === 'draw_polygon',
      },
      {
        id: 'clear',
        label: 'Clear geometry',
        icon: <Trash2 className="h-4 w-4" />,
        tone: 'warning',
      },
    ],
    [activeToolId],
  );

  const handleToolAction = useCallback((actionId: string) => {
    if (actionId === 'clear') {
      clearGeometry();
      return;
    }
    setActiveToolId(actionId as MapBuiltInToolId);
  }, [clearGeometry]);

  return (
    <div className="space-y-3">
      <DescriptionText className="text-xs">{hintText}</DescriptionText>
      <DescriptionText className="text-xs text-muted-foreground">{infrastructureHintText}</DescriptionText>

      <div className="h-[420px]">
        <MapAuthoringView
          className="h-full"
          styleJson={styleJson}
          status={styleJson ? 'success' : 'loading'}
          defaultViewState={initialViewState}
          inspectableLayerIds={inspectableLayerIds}
          activeToolId={activeToolId}
          drawnGeometry={drawnGeometry}
          toolItems={toolItems}
          onToolAction={handleToolAction}
          onToolRuntimeEvent={handleToolRuntimeEvent}
          footerSlot={<DescriptionText className="text-xs">{hintText}</DescriptionText>}
        />
      </div>
    </div>
  );
}
